import React from 'react';
import DepartmentProfileSwitcher from './DepartmentProfileSwitcher';
import sanitizeDepartment from 'utils/models/sanitizeDepartment';
import CogIcon from 'components/pure/utils/icon/CogIcon';
import ClickToEditField from 'components/pure/utils/form/ClickToEditField';
import hasFeature from 'utils/hasFeature';
import {Link} from 'react-router';
import './DepartmentForm.styl';
import InputField from 'components/pure/utils/form/InputField';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import RadioField from 'components/pure/utils/form/RadioField';
import {
  departmentProfiles,
  EXTERNAL_SHARE_ALLOW_ALL,
  EXTERNAL_SHARE_PREVENT_ALL,
  EXTERNAL_SHARE_DOMAIN_ONLY,
} from 'utils/const';

// The values in these const options are DB enums
const INTERNAL_SHARE_OPTIONS = [
  {
    value: 'allow_all',
    label: 'Allow users to share calls internally to all recipients',
  },
  {
    value: 'allow_if_recipient_can_view',
    label:
      "Prevent users from sharing calls internally unless they also have the ability to view the recipient's calls",
  },
];

const EXTERNAL_SHARE_OPTIONS = [
  {
    value: EXTERNAL_SHARE_ALLOW_ALL,
    label: 'Allow sharing calls by emails',
  },
  {
    value: EXTERNAL_SHARE_PREVENT_ALL,
    label: 'Prevent users from sharing calls by emails',
  },
  {
    value: EXTERNAL_SHARE_DOMAIN_ONLY,
    label: 'Prevent users from sharing calls by emails (unless recipient in same company domain)',
  },
];

const TWO_PARTY_CONSENT_OPTIONS = [
  {
    value: 'all_calls',
    getLabel: () =>
      'Process and diarize all calls, whether or not they are to/from a two party consent area code',
  },
  {
    value: 'area_code_should_not_be_diarized',
    getLabel: () => 'Calls to or from two party consent area codes should not be diarized by Coach',
  },
  {
    value: 'area_code_should_not_be_processed',
    getLabel: () =>
      'Calls to or from two party consent area codes should not be processed by Coach',
  },
];

const departmentProfileTypes = Object.keys(departmentProfiles);
const featureFlag = key => departmentProfiles[key].flag;

export default class DepartmentForm extends React.PureComponent {
  getError(property) {
    return this.props.stagingErrors &&
      this.props.stagingErrors[property] &&
      this.props.toggledFields[property]
      ? this.props.stagingErrors[property][0]
      : null;
  }

  getProfileLabel(type) {
    const pluralizedName = departmentProfiles[type].pluralized;
    const profile = this.props[pluralizedName].find(({id}) => {
      return id === this.props.department[`${type}Id`];
    });
    return profile ? profile.name : 'None';
  }

  render() {
    const {flags, stagingDepartment} = this.props;
    const clickToEditProps = {
      model: this.props.department,
      stagingModel: stagingDepartment,
      onSave: (field, department) =>
        this.props.commitStagingField(field, sanitizeDepartment(department)),
      onCancel: this.props.discardStagingField,
      onToggleEdit: this.props.toggleEditField,
      toggledFields: this.props.toggledFields,
      isSaving: this.props.isDepartmentSaving,
      errors: this.props.stagingErrors,
    };
    const internalSharesPermission = this.props.department.internalSharesPermission;
    const externalSharesPermission = this.props.department.externalSharesPermission;
    const processTwoPartyConsentCalls = this.props.department.processTwoPartyConsentCalls;

    const updateDepartment = this.props.updateDepartment;
    const department = this.props.department;

    return (
      <div className="department-form">
        <FieldContainer label="Department Name:" error={this.getError('name')}>
          <ClickToEditField className="field-value" field="name" {...clickToEditProps}>
            <InputField
              value={stagingDepartment.name || ''}
              placeholder="Name"
              focused={true}
              onChange={val => this.props.editStagingDepartment('name', val)}
            />
          </ClickToEditField>
        </FieldContainer>
        {departmentProfileTypes.map((type, i) => {
          const field = `${type}Id`;
          const route = routeOf(flags, stagingDepartment.id, departmentProfiles, type);
          return hasFeature(flags, featureFlag(type)) ? (
            <FieldContainer
              label={departmentProfiles[type].label + ' Profile:'}
              error={this.getError(type)}
              key={i}>
              <div className="field-value">
                <ClickToEditField
                  field={`${type}Id`}
                  readOnlyContent={this.getProfileLabel(type)}
                  {...clickToEditProps}>
                  <DepartmentProfileSwitcher
                    newProfileRoute={
                      // doesn't handle objects, and we don't have any place to send the user when
                      // it is an object (new topics admin route has no 'new' page).
                      typeof route === 'string' ? route : undefined
                    }
                    updateDepartment={department => {
                      this.props.editStagingDepartment(`${type}Id`, department[field]);
                    }}
                    department={stagingDepartment}
                    type={type}
                    options={this.props[departmentProfiles[type].pluralized]}
                  />
                </ClickToEditField>
                {!this.props.toggledFields[field] && (
                  <Link to={route}>
                    <CogIcon />
                  </Link>
                )}
              </div>
            </FieldContainer>
          ) : null;
        })}

        {/* start two-party-consent options */}
        <div className="options-box">
          <div className="header">Two party consent calls</div>
          <div className="content twoPartyConsentContent">
            {TWO_PARTY_CONSENT_OPTIONS.map(item => (
              <RadioField
                key={item.value}
                className="twoPartyConsentOption"
                label={item.getLabel()}
                checked={processTwoPartyConsentCalls === item.value}
                onClick={event =>
                  updateDepartment(department.set('processTwoPartyConsentCalls', item.value))
                }
              />
            ))}
          </div>
        </div>
        {/* end two-party-consent options */}

        {/* start sharing options */}
        <div className="options-box">
          <div className="header">Call sharing</div>
          <div className="content">
            <div className="name">Internal Shares</div>

            {INTERNAL_SHARE_OPTIONS.map(item => (
              <RadioField
                key={item.value}
                className="sharingOption"
                label={item.label}
                checked={internalSharesPermission === item.value}
                onClick={event =>
                  updateDepartment(department.set('internalSharesPermission', item.value))
                }
              />
            ))}
          </div>
          <div className="content">
            <div className="name">External Shares</div>

            {EXTERNAL_SHARE_OPTIONS.map(item => (
              <RadioField
                key={item.value}
                className="sharingOption"
                label={item.label}
                checked={externalSharesPermission === item.value}
                onClick={event =>
                  updateDepartment(department.set('externalSharesPermission', item.value))
                }
              />
            ))}
          </div>
        </div>
        {/* end sharing options */}
      </div>
    );
  }
}

export function routeOf(flags, departmentId, departmentProfiles, type) {
  if (type !== 'filterWordsProfile') {
    return normalRouteOf(departmentId, departmentProfiles, type);
  }
  return {
    pathname: '/admin/topics',
    query: {
      openDepartmentId: departmentId,
    },
  };
}

function normalRouteOf(departmentId, departmentProfiles, type) {
  return `/admin/departments/${departmentId}/${departmentProfiles[type].pluralized}`;
}
